import { useState, useEffect } from "react";
import { X, Users, Check, AlertCircle } from "lucide-react";
import { FormErrors, RateFormData } from "../../components/types";
import { channelConfig, resellers } from "./data";

interface MessagingRateModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function MessagingRateModal({
  isOpen,
  setIsOpen,
}: MessagingRateModalProps) {
  const [formData, setFormData] = useState<RateFormData>({
    resellerId: "",
    resellerName: "",
    channel: "sms",
    category: "",
    currentRate: "",
    newRate: "",
    effectiveDate: "",
    notes: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const userRole = localStorage.getItem("userRole");
  // Mock reseller data with current rates for different channels and categories

  useEffect(() => {
    if (formData.resellerId && formData.channel) {
      const selectedReseller = resellers.find(
        (r) => r.id === formData.resellerId
      );
      if (selectedReseller) {
        setFormData((prev) => ({
          ...prev,
          resellerName: selectedReseller.name,
          category: "",
          currentRate: "",
        }));
      }
    }
  }, [formData.resellerId, formData.channel]);

  useEffect(() => {
    if (formData.resellerId && formData.channel && formData.category) {
      const selectedReseller = resellers.find(
        (r) => r.id === formData.resellerId
      );
      if (selectedReseller && selectedReseller.rates[formData.channel]) {
        const rate =
          selectedReseller.rates[formData.channel]?.[formData.category];
        setFormData((prev) => ({
          ...prev,
          currentRate: rate ? rate.toFixed(4) : "",
        }));
      }
    }
  }, [formData.resellerId, formData.channel, formData.category]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.resellerId) {
      newErrors.resellerId = "Please select a reseller";
    }

    if (!formData.channel) {
      newErrors.channel = "Please select a messaging channel";
    }

    if (!formData.category) {
      newErrors.category = "Please select a category";
    }

    if (!formData.newRate) {
      newErrors.newRate = "New rate is required";
    } else if (isNaN(parseFloat(formData.newRate))) {
      // Added missing parenthesis
      newErrors.newRate = "Please enter a valid number";
    } else if (parseFloat(formData.newRate) <= 0) {
      newErrors.newRate = "Please enter a rate greater than 0";
    } else if (parseFloat(formData.newRate) > 2) {
      newErrors.newRate = "Rate cannot exceed $2.00 per unit";
    }

    if (!formData.effectiveDate) {
      newErrors.effectiveDate = "Effective date is required";
    } else {
      const selectedDate = new Date(formData.effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.effectiveDate = "Effective date cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsLoading(false);
    setShowSuccess(true);

    // Reset form after success
    setTimeout(() => {
      setShowSuccess(false);
     handleClose();
      resetForm();
    }, 2000);
  };

  if (!isOpen) return null;

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const resetForm = () => {
    setFormData({
      resellerId: "",
      resellerName: "",
      channel: "sms",
      category: "",
      currentRate: "",
      newRate: "",
      effectiveDate: "",
      notes: "",
    });
    setErrors({});
    setShowSuccess(false);
  };

  const rateChange =
    formData.currentRate && formData.newRate
      ? (
          ((parseFloat(formData.newRate) - parseFloat(formData.currentRate)) /
            parseFloat(formData.currentRate)) *
          100
        ).toFixed(1)
      : "0";

  const currentChannelConfig = channelConfig[formData.channel];
  const availableCategories = currentChannelConfig?.categories || [];
  const selectedCategory = availableCategories.find(
    (cat) => cat.value === formData.category
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="w-7 ml-3.5 h-7 text-blue-600"> ₦ </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Set Messaging Rates
              </h2>
              <p className="text-sm text-gray-500">
                Configure pricing across channels
              </p>
            </div>
          </div>
          <button
              onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Success State */}
        {showSuccess && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Rate Updated Successfully!
            </h3>
            <p className="text-gray-600">
              The new {currentChannelConfig?.name} rate has been set for the
              selected reseller.
            </p>
          </div>
        )}

        {/* Form */}
        {!showSuccess && (
          <div className="p-6 space-y-6">
            {/* Reseller Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select {userRole === "global_admin" ? `Reseller` : `Customer`}
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="resellerId"
                  value={formData.resellerId}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.resellerId ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  <option value="">
                    Choose a{" "}
                    {userRole === "global_admin" ? `Reseller` : `Customer`}...
                  </option>
                  {resellers.map((reseller) => (
                    <option key={reseller.id} value={reseller.id}>
                      {reseller.name} ({reseller.id})
                    </option>
                  ))}
                </select>
              </div>
              {errors.resellerId && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.resellerId}
                </p>
              )}
            </div>

            {/* Channel Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Messaging Channel
              </label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(channelConfig).map(([key, config]) => {
                  const IconComponent = config.icon;
                  const isSelected = formData.channel === key;
                  const colorClasses = {
                    blue: isSelected
                      ? "bg-blue-50 border-blue-200 text-blue-700"
                      : "border-gray-200 text-gray-600 hover:border-blue-200",
                    green: isSelected
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "border-gray-200 text-gray-600 hover:border-green-200",
                    purple: isSelected
                      ? "bg-purple-50 border-purple-200 text-purple-700"
                      : "border-gray-200 text-gray-600 hover:border-purple-200",
                  };

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        handleInputChange({
                          target: { name: "channel", value: key },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      className={`p-3 border-2 rounded-lg transition-colors text-center ${
                        colorClasses[config.color]
                      }`}
                    >
                      <IconComponent className="w-6 h-6 mx-auto mb-1" />
                      <div className="text-sm font-medium">{config.name}</div>
                    </button>
                  );
                })}
              </div>
              {errors.channel && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.channel}
                </p>
              )}
            </div>

            {/* Category Selection */}
            {formData.channel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentChannelConfig?.name} Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.category ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  <option value="">Select category...</option>
                  {availableCategories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {selectedCategory && (
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedCategory.description}
                  </p>
                )}
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.category}
                  </p>
                )}
              </div>
            )}

            {/* Current Rate Display */}
            {formData.currentRate && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Current Rate
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    ₦{parseFloat(formData.currentRate).toFixed(4)}/unit
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {currentChannelConfig?.name} • {selectedCategory?.label}
                </div>
              </div>
            )}

            {/* New Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Unit Rate
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400">
                  {" "}
                  ₦{" "}
                </span>
                <input
                  type="number"
                  name="newRate"
                  value={formData.newRate}
                  onChange={handleInputChange}
                  step="0.0001"
                  min="0"
                  max="2"
                  placeholder="0.0450"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.newRate ? "border-red-300" : "border-gray-300"
                  }`}
                />
              </div>
              {errors.newRate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.newRate}
                </p>
              )}
              {formData.newRate && formData.currentRate && (
                <p className="mt-1 text-sm text-gray-600">
                  {parseFloat(rateChange) > 0
                    ? "↑"
                    : parseFloat(rateChange) < 0
                    ? "↓"
                    : "="}
                  {Math.abs(parseFloat(rateChange))}% change from current rate
                </p>
              )}
            </div>

            {/* Effective Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective Date
              </label>
              <input
                type="date"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  errors.effectiveDate ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.effectiveDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.effectiveDate}
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Add any additional notes about this rate change..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                 onClick={handleClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  "Update Rate"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
